import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import * as Icon from 'react-feather';
import MainStructure from '../components/general/MainStructure';
import { useAuth } from '../AuthContext';
import TimezoneSelector from './components/TimezoneSelector';
import instagramLogo from '../assets/instagram.png';
import 'react-datepicker/dist/react-datepicker.css';

// An Instagram story is one image or one video. Instagram's publishing API
// takes the media only: no caption, stickers, links, music or polls. Images
// that are not 9:16 are placed whole over a blurred copy of themselves by the
// scheduler, and the preview here shows exactly that.

const UPLOAD_IMAGE_URL = 'https://api.schedpilot.com/wp-json/custom/v1/upload-image';
const UPLOAD_VIDEO_URL = 'https://api.schedpilot.com/wp-json/custom/v1/upload-video';
const CREATE_URL = 'https://api.schedpilot.com/wp-json/api/v1/create-instagram-story';

const VIDEO_MIN_S = 3;
const VIDEO_MAX_S = 60;
const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
const NOTE_MAX = 300;

const formatLocalDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const readVideoDuration = (file) => new Promise((resolve) => {
  const v = document.createElement('video');
  v.preload = 'metadata';
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(null);
  v.src = URL.createObjectURL(file);
});

const card = { background: '#fff', border: '1px solid #e8edf2', borderRadius: 14, padding: 20, marginBottom: 16 };
const label = { fontWeight: 700, fontSize: '0.95rem', marginBottom: 4, color: '#0f172a' };
const hint = { fontSize: '0.78rem', color: '#64748b', marginBottom: 12 };

function Avatar({ src, name, size = 34 }) {
  const [failed, setFailed] = useState(false);
  const letter = (name || '?')[0].toUpperCase();
  if (failed || !src) {
    return <div style={{ width: size, height: size, borderRadius: '50%', background: '#fce7f3', color: '#9d174d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.4, flexShrink: 0 }}>{letter}</div>;
  }
  return <img src={src} alt={name} onError={() => setFailed(true)} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
}

/* A phone-sized story frame. Images are shown the way the scheduler will
   render them: the whole picture over a blurred, darkened copy of itself. */
function StoryPreview({ media, account }) {
  const src = media?.preview || media?.url;
  return (
    <div style={{ width: '100%', maxWidth: 300, margin: '0 auto', aspectRatio: '9 / 16', borderRadius: 22, overflow: 'hidden', position: 'relative', background: '#0f172a', boxShadow: '0 12px 32px rgba(15,23,42,0.25)' }}>
      {src && media.kind === 'image' && (
        <>
          <div style={{ position: 'absolute', inset: -30, backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(22px) brightness(0.6)' }} />
          <img src={src} alt="Story preview" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
        </>
      )}
      {src && media.kind === 'video' && (
        <video src={src} autoPlay muted loop playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
      )}
      {!src && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.85rem', gap: 8 }}>
          <Icon.Smartphone size={28} />Your story appears here
        </div>
      )}

      {/* Instagram's story chrome, for orientation */}
      <div style={{ position: 'absolute', top: 10, left: 10, right: 10, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.45)' }}>
        <div style={{ width: '35%', height: '100%', borderRadius: 2, background: '#fff' }} />
      </div>
      <div style={{ position: 'absolute', top: 20, left: 12, right: 12, display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: '0.78rem', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
        <Avatar src={account?.image} name={account?.name || 'You'} size={26} />
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{account?.name || 'your_account'}</span>
        <span style={{ opacity: 0.75, fontWeight: 400 }}>now</span>
      </div>
    </div>
  );
}

function InstagramStoryPost() {
  const myContext = useAuth();
  const navigate = useNavigate();

  const instagramAccounts = (myContext.accounts || []).filter((a) => a.type === 'instagram');

  const [media, setMedia] = useState(null);   // { kind, status, id, url, preview, file, progress }
  const [note, setNote] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [submitting, setSubmitting] = useState(false);

  const mediaRef = useRef(null);
  mediaRef.current = media;
  useEffect(() => () => { if (mediaRef.current?.preview) URL.revokeObjectURL(mediaRef.current.preview); }, []);

  const upload = useCallback(async (file, kind) => {
    const form = new FormData();
    form.append(kind, file);
    try {
      const res = await axios.post(kind === 'video' ? UPLOAD_VIDEO_URL : UPLOAD_IMAGE_URL, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        onUploadProgress: (e) => {
          if (e.total) setMedia((m) => (m && m.file === file ? { ...m, progress: Math.round((e.loaded * 100) / e.total) } : m));
        },
      });
      const id = kind === 'video' ? res.data.video_id : res.data.image_id;
      setMedia((m) => (m && m.file === file
        ? (res.data.code === 200 && id ? { ...m, status: 'done', id, url: res.data.file_url } : { ...m, status: 'error' })
        : m));
      if (res.data.code !== 200) toast.error(res.data.message || 'Upload failed.');
    } catch (e) {
      setMedia((m) => (m && m.file === file ? { ...m, status: 'error' } : m));
      toast.error(e.response?.data?.message || 'Upload failed. Please try again.');
    }
  }, []);

  const onDrop = useCallback(async (accepted, rejected) => {
    if (rejected.length && !accepted.length) return toast.error('Use one JPG, PNG or WebP image, or one MP4 or MOV video.');
    const file = accepted[0];
    if (!file) return;
    if (accepted.length > 1) toast.info('A story is one image or one video, so only the first file was used.');

    const kind = file.type.startsWith('video/') ? 'video' : 'image';

    // Check Instagram's limits before uploading, not after a long upload.
    if (kind === 'video') {
      if (file.size > VIDEO_MAX_BYTES) return toast.error('Story videos can be at most 100 MB.');
      const duration = await readVideoDuration(file);
      if (duration !== null && (duration < VIDEO_MIN_S || duration > VIDEO_MAX_S)) {
        return toast.error(`Story videos must be ${VIDEO_MIN_S} to ${VIDEO_MAX_S} seconds long. This one is ${Math.round(duration)} seconds.`);
      }
    }

    if (mediaRef.current?.preview) URL.revokeObjectURL(mediaRef.current.preview);
    setMedia({ kind, status: 'uploading', id: null, url: null, preview: URL.createObjectURL(file), file, progress: 0 });
    upload(file, kind);
  }, [upload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [], 'video/mp4': [], 'video/quicktime': [] },
  });

  const removeMedia = () => {
    if (media?.preview) URL.revokeObjectURL(media.preview);
    setMedia(null);
  };

  const toggleAccount = (value) => setSelectedAccounts((prev) => (
    prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
  ));

  const problems = [];
  if (!media) problems.push('Add an image or a video');
  else if (media.status === 'uploading') problems.push('Wait for the upload to finish');
  else if (media.status === 'error') problems.push('The upload failed — remove it and try again');
  if (selectedAccounts.length === 0) problems.push('Choose an Instagram account');
  if (note.length > NOTE_MAX) problems.push(`Shorten the note to ${NOTE_MAX} characters`);

  const submit = async (postStatus) => {
    if (problems.length) return toast.error(problems[0]);

    const m = Array.isArray(myContext.membership) ? myContext.membership[0] : myContext.membership;
    const isLifetime = m?.plan?.type === 'lifetime' || (
      m?.end_date_time ? new Date(m.end_date_time.replace(' ', 'T') + 'Z').getTime() > Date.now() + 10 * 365 * 24 * 3600 * 1000 : false
    );
    if (!myContext.isValidMembership() && !isLifetime) return toast.error('Your membership is expired. It cannot post.');

    setSubmitting(true);
    try {
      const response = await fetch(CREATE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          media: { kind: media.kind, id: media.id },
          accounts: selectedAccounts,
          note: note.trim(),
          scheduledDate: formatLocalDate(selectedDate),
          timezone,
          postStatus,
        }),
      });
      const res = await response.json();
      if (res.code !== 200) {
        toast.error(res.message || 'Could not schedule the story.');
        return;
      }
      if (postStatus === 'draft') {
        toast.success('Story saved as a draft.');
        if (myContext.addDraftItem) myContext.addDraftItem({ pid: res.data?.id, content: res.data?.content, scheduled_date: selectedDate });
        navigate('/drafts');
      } else {
        toast.success('Story scheduled for Instagram.');
        if (myContext.addScheduledItem) myContext.addScheduledItem(res.data);
        navigate('/schedule-posts');
      }
    } catch (e) {
      toast.error('Error connecting to the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const previewAccount = instagramAccounts.find((a) => selectedAccounts.includes(`${a.id}_${a.type}`)) || instagramAccounts[0];
  const blocked = submitting || problems.length > 0;

  return (
    <MainStructure activeItem={'write-post'}>
      <div className="container p-0">
        {myContext.returnMembershipNotification && myContext.returnMembershipNotification()}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button type="button" onClick={() => navigate('/write-post')} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 500 }}>
            <Icon.ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 style={{ fontWeight: 800, fontSize: '1.4rem', margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src={instagramLogo} alt="" style={{ width: 22, height: 22 }} /> Instagram Story
            </h1>
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>One image or video, published as a story at the time you choose.</div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-7">

            {/* Media */}
            <div style={card}>
              <div style={label}>Story image or video</div>
              <div style={hint}>Portrait 9:16 (1080×1920) fills the screen. Other shapes are shown whole over a blurred background. Videos must be {VIDEO_MIN_S}–{VIDEO_MAX_S} seconds.</div>

              {media ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, border: '1px solid ' + (media.status === 'error' ? '#fecaca' : '#e2e8f0'), borderRadius: 12, padding: 12 }}>
                  <div style={{ width: 54, height: 96, borderRadius: 8, overflow: 'hidden', background: '#0f172a', flexShrink: 0 }}>
                    {media.kind === 'image'
                      ? <img src={media.preview || media.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <video src={media.preview || media.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{media.file?.name || (media.kind === 'video' ? 'Video' : 'Image')}</div>
                    <div style={{ fontSize: '0.78rem', color: media.status === 'error' ? '#b91c1c' : '#64748b' }}>
                      {media.status === 'uploading' && `Uploading… ${media.progress || 0}%`}
                      {media.status === 'done' && (media.kind === 'video' ? 'Video ready' : 'Image ready')}
                      {media.status === 'error' && 'Upload failed'}
                    </div>
                    {media.status === 'uploading' && (
                      <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, marginTop: 6 }}>
                        <div style={{ width: `${media.progress || 0}%`, height: '100%', background: '#d62976', borderRadius: 2, transition: 'width .2s' }} />
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={removeMedia} aria-label="Remove media" style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '6px 10px', color: '#475569', cursor: 'pointer', fontSize: '0.8rem' }}>
                    Replace
                  </button>
                </div>
              ) : (
                <div {...getRootProps()} style={{ border: `2px dashed ${isDragActive ? '#d62976' : '#cbd5e1'}`, background: isDragActive ? '#fdf2f8' : '#f8fafc', borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer' }}>
                  <input {...getInputProps()} />
                  <Icon.UploadCloud size={28} color="#d62976" />
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginTop: 6 }}>{isDragActive ? 'Drop it here' : 'Drag an image or video here, or click to choose'}</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>JPG, PNG, WebP · MP4 or MOV up to 100 MB</div>
                </div>
              )}
            </div>

            {/* What the API cannot do */}
            <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', gap: 10, fontSize: '0.8rem', color: '#78350f' }}>
              <Icon.Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                Instagram only lets apps publish the image or video itself. Stickers, links, music, polls and on-screen text from the Instagram app aren't available for scheduled stories, so put any text into the image before you upload it. Needs a Business or Creator account.
              </div>
            </div>

            {/* Accounts */}
            <div style={card}>
              <div style={label}>Post to</div>
              {instagramAccounts.length === 0 ? (
                <div style={{ ...hint, marginBottom: 0 }}>
                  No Instagram account connected yet.{' '}
                  <button type="button" onClick={() => navigate('/social-accounts')} style={{ border: 'none', background: 'none', padding: 0, color: '#d62976', fontWeight: 600, cursor: 'pointer' }}>Connect Instagram</button>
                </div>
              ) : instagramAccounts.map((a) => {
                const value = `${a.id}_${a.type}`;
                const on = selectedAccounts.includes(value);
                return (
                  <label key={value} htmlFor={`ig-acc-${value}`} style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8, padding: '8px 10px', marginBottom: 4, cursor: 'pointer', background: on ? '#fdf2f8' : 'transparent', border: on ? '1px solid #fbcfe8' : '1px solid transparent' }}>
                    <Avatar src={a.image} name={a.name} />
                    <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
                    <input id={`ig-acc-${value}`} type="checkbox" checked={on} onChange={() => toggleAccount(value)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  </label>
                );
              })}
            </div>

            {/* Note */}
            <div style={card}>
              <div style={label}>Calendar note <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span></div>
              <div style={hint}>Only for you — it labels the story in your calendar and post lists. Stories have no caption, so this is never published.</div>
              <input type="text" className="form-control" value={note} maxLength={NOTE_MAX + 20} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Friday launch teaser" />
            </div>

            {/* Schedule */}
            <div style={card}>
              <div style={label}>When</div>
              <DatePicker selected={selectedDate} onChange={(d) => setSelectedDate(d)} showTimeSelect dateFormat="MMMM d, yyyy h:mm aa" className="form-control mb-3" />
              <TimezoneSelector value={timezone} onChange={setTimezone} label="Scheduling timezone" />

              {problems.length > 0 && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: '0.8rem', color: '#475569' }}>
                  {problems.map((p) => <div key={p} style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Icon.Circle size={8} /> {p}</div>)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button type="button" disabled={blocked} onClick={() => submit('publish')}
                  style={{ flex: 1, background: 'linear-gradient(90deg,#feda75,#d62976 45%,#962fbf)', border: 'none', borderRadius: 9, padding: '11px 0', fontWeight: 600, fontSize: '0.9rem', color: '#fff', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.55 : 1 }}>
                  {submitting ? 'Scheduling…' : 'Schedule story'}
                </button>
                <button type="button" disabled={blocked} onClick={() => submit('draft')}
                  style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 9, padding: '11px 16px', fontWeight: 600, fontSize: '0.9rem', color: '#334155', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.55 : 1 }}>
                  Save draft
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div style={{ position: 'sticky', top: 16 }}>
              <div style={{ ...label, marginBottom: 8 }}>Preview</div>
              <StoryPreview media={media} account={previewAccount} />
            </div>
          </div>
        </div>
      </div>
    </MainStructure>
  );
}

export default InstagramStoryPost;
